import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchRolloverCount, uploadFormData } from './UploadDialog.api';
import { LastSuccessfulCalculationDetails, UploadImportFormData } from './UploadImportFormData.types';
import { AppDispatch, RootState } from '../../app/store';
import { setDatasetDetails } from '../../core/Slices/Dataset.slice';
import { DatasetDetailsResponse, GetDisplayMessageForImportStatus } from '../../core/Types/DatasetDetailsResponse.types';
import { Messages } from '../../Constants/Messages';
import { showErrorSnackbar, showSuccessSnackbar } from '../../component/snackbar/Snackbar.slice';
import { handleGetStatusDetail } from '../../component/status-widget/statusWidget.slice';

interface State {
	isUploadDialogVisible: boolean;
	isRolloverAvailable: boolean;
}

const initialState: State = {
	isUploadDialogVisible: false,
	isRolloverAvailable: false,
};

export const uploadImportFormData = createAsyncThunk<
	void,
	UploadImportFormData,
	{
		dispatch: AppDispatch;
		state: RootState;
	}
>('uploadImport/uploadImportFormData', async (params: UploadImportFormData, thunkAPI) => {
	const response = await uploadFormData(params);
	if (!response.ok) {
		showErrorSnackbar(
			thunkAPI.dispatch,
			Messages.UPLOAD_FILE_FAILED
		);
		return;
	}

	const datasetDetails = JSON.parse(await response.text()) as DatasetDetailsResponse
	thunkAPI.dispatch(setDatasetDetails(datasetDetails))
	thunkAPI.dispatch(uploadDialogClosed());
	const displayMessage = GetDisplayMessageForImportStatus(datasetDetails.status);
	thunkAPI.dispatch(handleGetStatusDetail(params.jobId));

	if (displayMessage) {
		showSuccessSnackbar(
			thunkAPI.dispatch,
			displayMessage
		)
	} else {
		showErrorSnackbar(
			thunkAPI.dispatch,
			Messages.UPLOAD_FILE_FAILED
		);
	}
});

export const fetchRolloverCountFromSettingsReport = createAsyncThunk<void, void, { state: RootState; dispatch: AppDispatch }>(
	'uploadImport/fetchRolloverCount',
	async (_, thunkAPI) => {
		const jobId = thunkAPI.getState().cem.selectedJob.id;
		const response = await fetchRolloverCount(jobId);
		if (response.Success) {
			const details = JSON.parse(response.Data.toString()) as LastSuccessfulCalculationDetails;
			thunkAPI.dispatch(setRolloverAvailable(details.rolloverCount > 0));
		} else {
			thunkAPI.dispatch(setRolloverAvailable(false));
		}
	},
);

const uploadImportSlice = createSlice({
	name: 'uploadImport',
	initialState,
	reducers: {
		uploadDialogOpened(state): State {
			return { ...state, isUploadDialogVisible: true };
		},
		uploadDialogClosed(): State {
			return { ...initialState };
		},
		setRolloverAvailable(state, action) {
			return { ...state, isRolloverAvailable: action.payload };
		}
	},
});

export const { uploadDialogOpened, uploadDialogClosed, setRolloverAvailable } = uploadImportSlice.actions;

export default uploadImportSlice.reducer;